/**
 * @file useDocumentRowHighlight.ts
 * @description Переход к уже добавленной строке документа и её подсветка
 * (phase19.24 этап 1, шаг 1; решение пользователя 2026-09-20).
 *
 * Сценарий: в «Подборе» кладовщик жмёт фасовку с подписью «уже добавлена» —
 * страница прокручивает список к её строке и подсвечивает на пару секунд.
 * Без подсветки прокрутка выглядит как «ничего не произошло».
 *
 * Ищем строку по `data-row-id`, а не по `getElementById`: таблица (`≥lg`) и
 * карточки (`<lg`) живут в DOM одновременно, и один и тот же `id` был бы в
 * документе дважды. Берём первую ВИДИМУЮ (`offsetParent !== null`) — скрытую
 * ветку браузер не прокрутит.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** Сколько держится подсветка: хватает глазу, не мешает работе. */
const HIGHLIGHT_MS = 2500;

interface DocumentRowHighlight {
  /** id подсвеченной строки документа; null — подсветки нет. */
  highlightedId: string | null;
  /** `data-row-id` строки: `receipt-item-<id>`. */
  rowDomId: (itemId: string) => string;
  /** Прокрутить к строке и подсветить её. */
  goTo: (itemId: string) => void;
}

export function useDocumentRowHighlight(prefix: string): DocumentRowHighlight {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const rowDomId = useCallback(
    (itemId: string) => `${prefix}-${itemId}`,
    [prefix],
  );

  const goTo = useCallback(
    (itemId: string) => {
      setHighlightedId(itemId);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
      // Кадр ожидания: диалог ещё закрывается, и прокрутка под ним пропала бы.
      requestAnimationFrame(() => {
        const nodes = document.querySelectorAll<HTMLElement>(
          `[data-row-id="${rowDomId(itemId)}"]`,
        );
        const target =
          Array.from(nodes).find((node) => node.offsetParent !== null) ?? nodes[0];
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    [rowDomId],
  );

  return { highlightedId, rowDomId, goTo };
}
